import {useEffect, useRef, useState} from 'react';
import {Box, IconButton, Table, TableBody, TableCell, TableRow, Typography} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import type {TaxBasicProfitsVO, TaxExtraItem} from '@/services/auditService/interface/tax-com';
import {InputField, MoneyField} from '@/components/common';
import {amountCellSx, descCellSx, labelCell} from '@/pages/components/tax-computation/style/tax-style';

const extraLabelCellSx = {...labelCell, py: 0};
const extraDescCellSx = {...descCellSx, py: 0};
const extraAmountCellSx = {...amountCellSx, py: 0, paddingRight: 0.5};
/** Match MoneyField editable InputField height (compact + tiny Box). */
const extraFieldSx = {
    '& .MuiFormHelperText-root': {
        position: 'absolute',
        m: 0,
        lineHeight: 1.2,
    },
};

interface TaxMainBasicProps {
    data?: TaxBasicProfitsVO | null;
    currency?: string | null;
    /** When true, show ADD/LESS even if empty and allow user extra rows */
    editableExtras?: boolean;
    extraAddItems?: TaxExtraItem[];
    extraLessItems?: TaxExtraItem[];
    onExtraAddChange?: (items: TaxExtraItem[]) => void;
    onExtraLessChange?: (items: TaxExtraItem[]) => void;
}

function rowKey(item: TaxExtraItem, idx: number): string {
    return item.clientKey || (item.id != null ? `id-${item.id}` : `idx-${idx}`);
}

function newExtraRow(section: 'ADD' | 'LESS'): TaxExtraItem {
    return {
        clientKey: `${section}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        section,
        itemName: '',
        amount: 0,
    };
}

/** Draft locally while focused so parent save/refresh cannot drop keystrokes. */
function ExtraItemNameField({
    value,
    onCommit,
}: {
    value: string;
    onCommit: (next: string) => void;
}) {
    const [draft, setDraft] = useState(value);
    const focusedRef = useRef(false);

    useEffect(() => {
        if (!focusedRef.current) {
            setDraft(value);
        }
    }, [value]);

    return (
        <Box sx={{padding: 0, width: '100%', position: 'relative'}}>
            <InputField
                compact
                fullWidth
                placeholder="Description"
                value={draft}
                error={!draft.trim()}
                helperText={!draft.trim() ? 'Name required' : undefined}
                onFocus={() => {
                    focusedRef.current = true;
                }}
                onChange={(e) => {
                    const next = e.target.value;
                    setDraft(next);
                    onCommit(next);
                }}
                onBlur={() => {
                    focusedRef.current = false;
                    if (draft !== value) {
                        onCommit(draft);
                    }
                }}
                sx={{width: '100%', maxWidth: '100%', ...extraFieldSx}}
            />
        </Box>
    );
}

export function TaxMainBasic({
    data,
    currency,
    editableExtras = false,
    extraAddItems,
    extraLessItems,
    onExtraAddChange,
    onExtraLessChange,
}: TaxMainBasicProps) {
    const addExtras = extraAddItems ?? data?.extraAddItems ?? [];
    const lessExtras = extraLessItems ?? data?.extraLessItems ?? [];

    const shouldRenderAmountRow = (value?: number) => {
        const num = Number(value ?? 0);
        if (!Number.isFinite(num)) return false;
        return num !== 0;
    };

    const shouldRenderAddRow = editableExtras || [
        data?.interestExpense,
        data?.penaltiesFinesStampDuties,
        data?.donationCharitable,
        data?.companySetUpFee,
        data?.depreciationExpenses,
        data?.lossDisposalOfMachineryPlant,
        data?.provisionDoubtful,
        data?.exchangeLoss,
        data?.balancingCharge,
        data?.withholdingTax,
        ...(data?.scheduleAddBacks ?? []).map((item) => item?.addBackAmount),
        ...addExtras.map((item) => item?.amount),
    ].some(shouldRenderAmountRow) || addExtras.length > 0;

    const shouldRenderLessRow = editableExtras || [
        data?.netInterestIncome,
        data?.subsidiesFromGovernment,
        data?.purchaseCostHardSoftware,
        data?.gainDisposalOfFixedAssets,
        data?.capitalExpenditure,
        data?.depreciation,
        data?.dividendIncome,
        data?.industrialBuilding,
        data?.commercialBuilding,
        data?.exchangeGain,
        data?.balancingAllowance,
        data?.excessWithholdingTax,
        ...(data?.scheduleLessBacks ?? []).map((item) => item?.addBackAmount),
        ...lessExtras.map((item) => item?.amount),
    ].some(shouldRenderAmountRow) || lessExtras.length > 0;

    const renderAmount = (value?: number) => {
        if (value === undefined || value === null) return null;
        return (
            <MoneyField
                value={value}
                editable={false}
                currencySymbol={currency || ''}
                showCents={false}
                align="right"
            />
        );
    };

    const updateAdd = (next: TaxExtraItem[]) => onExtraAddChange?.(next);
    const updateLess = (next: TaxExtraItem[]) => onExtraLessChange?.(next);

    const handleAddAmount = (idx: number, raw: number) => {
        const amount = Number.isFinite(raw) ? Math.max(0, raw) : 0;
        updateAdd(addExtras.map((item, i) => (i === idx ? {...item, amount} : item)));
    };

    const handleLessAmount = (idx: number, raw: number) => {
        let amount = Number.isFinite(raw) ? raw : 0;
        if (amount > 0) amount = -amount;
        updateLess(lessExtras.map((item, i) => (i === idx ? {...item, amount} : item)));
    };

    const profitLossLabel = (data?.perIncomeStatement ?? 0) > 0 ? 'PROFIT:' : 'LOSS:';

    return (
        <Table size="small">
            <TableBody>
                {shouldRenderAmountRow(data?.perIncomeStatement) ? (
                    <TableRow>
                        <TableCell sx={labelCell}>{profitLossLabel}</TableCell>
                        <TableCell sx={descCellSx}>Per Income Statement attached herewith</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.perIncomeStatement)}</TableCell>
                    </TableRow>
                ) : null}

                <TableRow>
                    <TableCell colSpan={3} sx={descCellSx}/>
                </TableRow>

                {shouldRenderAddRow ? (
                    <>
                        <TableRow>
                            <TableCell sx={labelCell}>ADD:</TableCell>
                            <TableCell sx={descCellSx} colSpan={2}/>
                        </TableRow>

                        {shouldRenderAmountRow(data?.interestExpense) ? (
                            <TableRow>
                                <TableCell sx={labelCell}/>
                                <TableCell sx={descCellSx}>Interest expenses(capital in nature)</TableCell>
                                <TableCell sx={amountCellSx} align="right">{renderAmount(data?.interestExpense)}</TableCell>
                            </TableRow>
                        ) : null}

                        {shouldRenderAmountRow(data?.penaltiesFinesStampDuties) ? (
                            <TableRow>
                                <TableCell sx={labelCell}/>
                                <TableCell sx={descCellSx}>Penalties, fines and stamp duties</TableCell>
                                <TableCell sx={amountCellSx} align="right">{renderAmount(data?.penaltiesFinesStampDuties)}</TableCell>
                            </TableRow>
                        ) : null}

                        {shouldRenderAmountRow(data?.donationCharitable) ? (
                            <TableRow>
                                <TableCell sx={labelCell}/>
                                <TableCell sx={descCellSx}>Donations made to approved charitable organizations</TableCell>
                                <TableCell sx={amountCellSx} align="right">{renderAmount(data?.donationCharitable)}</TableCell>
                            </TableRow>
                        ) : null}

                        {shouldRenderAmountRow(data?.companySetUpFee) ? (
                            <TableRow>
                                <TableCell sx={labelCell}/>
                                <TableCell sx={descCellSx}>Campany set-up fees</TableCell>
                                <TableCell sx={amountCellSx} align="right">{renderAmount(data?.companySetUpFee)}</TableCell>
                            </TableRow>
                        ) : null}

                        {shouldRenderAmountRow(data?.depreciationExpenses) ? (
                            <TableRow>
                                <TableCell sx={labelCell}/>
                                <TableCell sx={descCellSx}>Depreciation expenses</TableCell>
                                <TableCell sx={amountCellSx} align="right">{renderAmount(data?.depreciationExpenses)}</TableCell>
                            </TableRow>
                        ) : null}

                        {shouldRenderAmountRow(data?.lossDisposalOfMachineryPlant) ? (
                            <TableRow>
                                <TableCell sx={labelCell}/>
                                <TableCell sx={descCellSx}>
                                    Loss on disposal of machinery or plant, decoration, furniture and other fix assets
                                </TableCell>
                                <TableCell sx={amountCellSx} align="right">
                                    {renderAmount(data?.lossDisposalOfMachineryPlant)}
                                </TableCell>
                            </TableRow>
                        ) : null}

                        {shouldRenderAmountRow(data?.provisionDoubtful) ? (
                            <TableRow>
                                <TableCell sx={labelCell}/>
                                <TableCell sx={descCellSx}>Provision for doubtful debts</TableCell>
                                <TableCell sx={amountCellSx} align="right">{renderAmount(data?.provisionDoubtful)}</TableCell>
                            </TableRow>
                        ) : null}

                        {shouldRenderAmountRow(data?.exchangeLoss) ? (
                            <TableRow>
                                <TableCell sx={labelCell}/>
                                <TableCell sx={descCellSx}>Exchange loss (Capital in nature)</TableCell>
                                <TableCell sx={amountCellSx} align="right">{renderAmount(data?.exchangeLoss)}</TableCell>
                            </TableRow>
                        ) : null}

                        {shouldRenderAmountRow(data?.balancingCharge) ? (
                            <TableRow>
                                <TableCell sx={labelCell}/>
                                <TableCell sx={descCellSx}>Balancing charge</TableCell>
                                <TableCell sx={amountCellSx} align="right">{renderAmount(data?.balancingCharge)}</TableCell>
                            </TableRow>
                        ) : null}

                        {shouldRenderAmountRow(data?.withholdingTax) ? (
                            <TableRow>
                                <TableCell sx={labelCell}/>
                                <TableCell sx={descCellSx}>Withholding Tax</TableCell>
                                <TableCell sx={amountCellSx} align="right">{renderAmount(data?.withholdingTax)}</TableCell>
                            </TableRow>
                        ) : null}

                        {data?.scheduleAddBacks
                            ?.filter((item) => shouldRenderAmountRow(item?.addBackAmount))
                            .slice()
                            .sort((a, b) => (a?.nameEn ?? '').localeCompare(b?.nameEn ?? '', undefined, {sensitivity: 'base'}))
                            .map((item) => (
                                <TableRow key={item.nameEn ?? item.displayName}>
                                    <TableCell sx={labelCell}/>
                                    <TableCell sx={descCellSx}>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: 2,
                                            flexWrap: 'wrap',
                                        }}>
                                            <Typography variant="body2" component="span">
                                                {item.displayName ?? `Non deductible ${item.nameEn ?? ''}`}
                                            </Typography>
                                            {item.formula ? (
                                                <Typography variant="body2" component="span" sx={{whiteSpace: 'nowrap'}}>
                                                    {item.formula}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={amountCellSx} align="right">
                                        {renderAmount(item.addBackAmount)}
                                    </TableCell>
                                </TableRow>
                            ))}

                        {addExtras.map((item, idx) => (
                            <TableRow key={rowKey(item, idx)}>
                                <TableCell sx={extraLabelCellSx}/>
                                <TableCell sx={extraDescCellSx}>
                                    {editableExtras ? (
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                            <ExtraItemNameField
                                                value={item.itemName ?? ''}
                                                onCommit={(itemName) => {
                                                    updateAdd(addExtras.map((row, i) => (i === idx ? {...row, itemName} : row)));
                                                }}
                                            />
                                            <IconButton
                                                size="small"
                                                aria-label="delete-extra-add-item"
                                                onClick={() => updateAdd(addExtras.filter((_, i) => i !== idx))}
                                            >
                                                <DeleteOutlineOutlinedIcon fontSize="small"/>
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Typography variant="body2">{item.itemName}</Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={extraAmountCellSx} align="right">
                                    {editableExtras ? (
                                        <MoneyField
                                            value={item.amount ?? 0}
                                            editable
                                            tiny
                                            allowNegative={false}
                                            showCents={false}
                                            currencySymbol={currency || ''}
                                            align="right"
                                            sx={extraFieldSx}
                                            onChangeNumber={(num) => handleAddAmount(idx, num)}
                                        />
                                    ) : renderAmount(item.amount)}
                                </TableCell>
                            </TableRow>
                        ))}

                        {editableExtras ? (
                            <TableRow>
                                <TableCell sx={extraLabelCellSx}/>
                                <TableCell sx={extraDescCellSx} colSpan={2}>
                                    <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                                        <IconButton
                                            size="small"
                                            aria-label="add-extra-add-item"
                                            onClick={() => updateAdd([...addExtras, newExtraRow('ADD')])}
                                        >
                                            <AddOutlinedIcon fontSize="small"/>
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : null}

                        <TableRow>
                            <TableCell colSpan={3} sx={descCellSx}/>
                        </TableRow>
                    </>
                ) : null}

                {shouldRenderLessRow ? (
                    <TableRow>
                        <TableCell sx={labelCell}>LESS:</TableCell>
                        <TableCell sx={descCellSx} colSpan={2}/>
                    </TableRow>
                ) : null}

                {shouldRenderAmountRow(data?.netInterestIncome) ? (
                    <TableRow>
                        <TableCell sx={labelCell}/>
                        <TableCell sx={descCellSx}>Net interest income exempted from payment of Profits Tax</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.netInterestIncome)}</TableCell>
                    </TableRow>
                ) : null}

                {shouldRenderAmountRow(data?.subsidiesFromGovernment) ? (
                    <TableRow>
                        <TableCell sx={labelCell}/>
                        <TableCell sx={descCellSx}>Subsidies From Government (deduct in tax computation)</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.subsidiesFromGovernment)}</TableCell>
                    </TableRow>
                ) : null}

                {shouldRenderAmountRow(data?.purchaseCostHardSoftware) ? (
                    <TableRow>
                        <TableCell sx={labelCell}/>
                        <TableCell sx={descCellSx}>Purchase cost of computer hardware and software</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.purchaseCostHardSoftware)}</TableCell>
                    </TableRow>
                ) : null}

                {shouldRenderAmountRow(data?.gainDisposalOfFixedAssets) ? (
                    <TableRow>
                        <TableCell sx={labelCell}/>
                        <TableCell sx={descCellSx}>Gain on disposal / sale of fixed assets</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.gainDisposalOfFixedAssets)}</TableCell>
                    </TableRow>
                ) : null}

                {shouldRenderAmountRow(data?.capitalExpenditure) ? (
                    <TableRow>
                        <TableCell sx={labelCell}/>
                        <TableCell sx={descCellSx}>Capital expenditure on environmental protection facilities</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.capitalExpenditure)}</TableCell>
                    </TableRow>
                ) : null}

                {shouldRenderAmountRow(data?.depreciation) ? (
                    <TableRow>
                        <TableCell sx={labelCell}/>
                        <TableCell sx={descCellSx}>Depreciation Allowance</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.depreciation)}</TableCell>
                    </TableRow>
                ) : null}

                {shouldRenderAmountRow(data?.dividendIncome) ? (
                    <TableRow>
                        <TableCell sx={labelCell}/>
                        <TableCell sx={descCellSx}>Dividend income</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.dividendIncome)}</TableCell>
                    </TableRow>
                ) : null}

                {shouldRenderAmountRow(data?.industrialBuilding) ? (
                    <TableRow>
                        <TableCell sx={labelCell}/>
                        <TableCell sx={descCellSx}>Industrial Building Allowance</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.industrialBuilding)}</TableCell>
                    </TableRow>
                ) : null}

                {shouldRenderAmountRow(data?.commercialBuilding) ? (
                    <TableRow>
                        <TableCell sx={labelCell}/>
                        <TableCell sx={descCellSx}>Commercial Building Allowance</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.commercialBuilding)}</TableCell>
                    </TableRow>
                ) : null}

                {shouldRenderAmountRow(data?.exchangeGain) ? (
                    <TableRow>
                        <TableCell sx={labelCell}/>
                        <TableCell sx={descCellSx}>Exchange gain (Capital in nature)</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.exchangeGain)}</TableCell>
                    </TableRow>
                ) : null}

                {shouldRenderAmountRow(data?.balancingAllowance) ? (
                    <TableRow>
                        <TableCell sx={labelCell}/>
                        <TableCell sx={descCellSx}>Balancing allowance</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.balancingAllowance)}</TableCell>
                    </TableRow>
                ) : null}

                {shouldRenderAmountRow(data?.excessWithholdingTax) ? (
                    <TableRow>
                        <TableCell sx={labelCell}/>
                        <TableCell sx={descCellSx}>
                            Excess withholding tax not eligible for tax credit, but deductible as an expense (**)
                        </TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.excessWithholdingTax)}</TableCell>
                    </TableRow>
                ) : null}

                {data?.scheduleLessBacks
                    ?.filter((item) => shouldRenderAmountRow(item?.addBackAmount))
                    .slice()
                    .sort((a, b) => (a?.nameEn ?? '').localeCompare(b?.nameEn ?? '', undefined, {sensitivity: 'base'}))
                    .map((item) => (
                        <TableRow key={`less-back-${item.nameEn ?? item.displayName}`}>
                            <TableCell sx={labelCell}/>
                            <TableCell sx={descCellSx}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                }}>
                                    <Typography variant="body2" component="span">
                                        {item.displayName ?? item.nameEn ?? ''}
                                    </Typography>
                                    {item.formula ? (
                                        <Typography variant="body2" component="span" sx={{whiteSpace: 'nowrap'}}>
                                            {item.formula}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </TableCell>
                            <TableCell sx={amountCellSx} align="right">
                                {renderAmount(item.addBackAmount)}
                            </TableCell>
                        </TableRow>
                    ))}

                {lessExtras.map((item, idx) => (
                    <TableRow key={rowKey(item, idx)}>
                        <TableCell sx={extraLabelCellSx}/>
                        <TableCell sx={extraDescCellSx}>
                            {editableExtras ? (
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <ExtraItemNameField
                                        value={item.itemName ?? ''}
                                        onCommit={(itemName) => {
                                            updateLess(lessExtras.map((row, i) => (i === idx ? {...row, itemName} : row)));
                                        }}
                                    />
                                    <IconButton
                                        size="small"
                                        aria-label="delete-extra-less-item"
                                        onClick={() => updateLess(lessExtras.filter((_, i) => i !== idx))}
                                    >
                                        <DeleteOutlineOutlinedIcon fontSize="small"/>
                                    </IconButton>
                                </Box>
                            ) : (
                                <Typography variant="body2">{item.itemName}</Typography>
                            )}
                        </TableCell>
                        <TableCell sx={extraAmountCellSx} align="right">
                            {editableExtras ? (
                                <MoneyField
                                    value={item.amount ?? 0}
                                    editable
                                    tiny
                                    allowNegative
                                    showCents={false}
                                    currencySymbol={currency || ''}
                                    align="right"
                                    sx={extraFieldSx}
                                    onChangeNumber={(num) => handleLessAmount(idx, num)}
                                />
                            ) : renderAmount(item.amount)}
                        </TableCell>
                    </TableRow>
                ))}

                {editableExtras && shouldRenderLessRow ? (
                    <TableRow>
                        <TableCell sx={extraLabelCellSx}/>
                        <TableCell sx={extraDescCellSx} colSpan={2}>
                            <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                                <IconButton
                                    size="small"
                                    aria-label="add-extra-less-item"
                                    onClick={() => updateLess([...lessExtras, newExtraRow('LESS')])}
                                >
                                    <AddOutlinedIcon fontSize="small"/>
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>
                ) : null}

                {shouldRenderAmountRow(data?.donationCharitable) ? (
                    <TableRow>
                        <TableCell colSpan={2} sx={descCellSx}></TableCell>
                        <TableCell sx={amountCellSx} align="right">
                            {data?.lessTotal === undefined || data?.lessTotal === null ? null : (
                                <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5}}>
                                    <Typography variant="body2">(Y)</Typography>
                                    {renderAmount(data.lessTotal)}
                                </Box>
                            )}
                        </TableCell>
                    </TableRow>
                ) : null}
            </TableBody>
        </Table>
    );
}
